import {
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  HStack,
  IconButton,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { DateRangeSelector } from '../../../Objects/Filter/DateRangeSelector/DateRangeSelector';
import { FormikInput } from '../../../common/Form/FormikInput';
import { useFormik } from 'formik';
import Joi from 'joi';
import { getErrors } from '../../../../services/Joi/JoiValidationService';
import React, { useCallback } from 'react';
import { ObjectsParams } from '../../../../types/ParamsTypes';
import { OwnBookingsBookingsData } from '../../../../types/AdminTypes';
import { insertOwnBooking, updateOwnBooking } from '../../../../services/Admin/OwnerService';
import { getCurrentToken, handleAuthentication } from '../../../../services/Admin/AuthService';
import { AiOutlineClose } from 'react-icons/all';
import { BookingEditParams } from '../../../../types/BookingTypes';

export interface BookingsFormProps {
  handleDirtyObjects: (value: boolean) => void;
  objectSelected: OwnBookingsBookingsData;
  objectsParams: ObjectsParams;
  handleDateRange: (updatedObjectsParams: ObjectsParams) => void;
  handleTokenState: (value: string | null) => void;
  bookingEditParams: BookingEditParams;
  handleBookingEditParams: (values: BookingEditParams) => void;
}

export const BookingsForm = (props: BookingsFormProps): JSX.Element => {
  const {
    handleDirtyObjects,
    objectSelected,
    objectsParams,
    handleDateRange,
    handleTokenState,
    bookingEditParams,
    handleBookingEditParams,
  } = props;
  const toast = useToast();

  const getLoginParamsFormValidationSchema = (): Joi.AnySchema => {
    return Joi.object().keys({
      Comment: Joi.string().allow('').required(),
    });
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: { Comment: bookingEditParams.BookingComment },
    onSubmit: (values, { resetForm }) => {
      (async function () {
        await handleSubmit(values);
      })();
    },
    validate: values => {
      return getErrors(getLoginParamsFormValidationSchema(), values);
    },
  });

  const handleSubmit = useCallback(
    async values => {
      if (!objectsParams.fromDate || !objectsParams.toDate) return null;
      try {
        await handleAuthentication('ADMIN_DETAILS', handleTokenState);

        const token = getCurrentToken('ADMIN_DETAILS');
        const baseBookingParams = {
          fromDate: objectsParams.fromDate,
          toDate: objectsParams.toDate,
          Object: objectSelected.Object,
          Comment: values.Comment,
        };

        const insertOwnBookingParams = baseBookingParams;
        const updateBookingParams = bookingEditParams.booking && {
          ...baseBookingParams,
          Booking: bookingEditParams.booking,
        };
        const actionBooking = () =>
          updateBookingParams
            ? updateOwnBooking(updateBookingParams, token)
            : insertOwnBooking(insertOwnBookingParams, token);

        await actionBooking();
        cancelEditingBooking();
        handleDirtyObjects(true);

        toast({
          position: 'top-right',
          title: bookingEditParams.booking
            ? 'Buchung erfolgreich aktualisiert.'
            : 'Buchung erfolgreich angelegt.',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        formik.setSubmitting(false);
      } catch {
        toast({
          position: 'top-right',
          title: bookingEditParams.booking
            ? 'Buchung konnte nicht aktualisiert werden.'
            : 'Buchung konnte nicht angelegt werden.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        formik.setSubmitting(false);
      }
    },
    [objectsParams],
  );

  const cancelEditingBooking = useCallback(() => {
    formik.resetForm();
    handleDateRange({
      ...objectsParams,
      fromDate: undefined,
      toDate: undefined,
    });
    handleBookingEditParams({
      booking: '',
      BookingComment: '',
      initialMonth: undefined,
    });
  }, []);

  return (
    <Flex
      as={'form'}
      id={'bookingForm'}
      // @ts-ignore
      onSubmit={formik.handleSubmit}
      bgColor={'white'}
      alignItems={'flex-start'}
      justifyContent={'center'}
      gridGap={4}
      flexDirection={'column'}>
      <Text w={'100%'} textAlign={'left'} fontSize={'xl'} textStyle={'category.primary'}>
        BUCHUNG EINTRAGEN/ÄNDERN
      </Text>
      <Flex width={'100%'} flexDirection={{ base: 'column', lg: 'row' }} gridGap={4}>
        <FormControl flex={1} width={'100%'}>
          <DateRangeSelector
            onFilterChange={handleDateRange}
            objectsParams={objectsParams}
            initialMonth={bookingEditParams.initialMonth}
          />
        </FormControl>
        <Flex flex={1} width={'100%'}>
          <FormikInput
            inputProps={{ h: '57px' }}
            type={'text'}
            placeholder={'Kommentar'}
            name={'Comment'}
            formik={formik}
          />
        </Flex>
        <ButtonGroup flex={{ lg: 1 }} h={'57px'}>
          <Button
            isLoading={formik.isSubmitting}
            disabled={!objectsParams.toDate || formik.isSubmitting}
            w={'100%'}
            h={'100%'}
            variant="primary"
            type={'submit'}
            _hover={{ bg: !objectsParams.toDate ? 'blue.500' : 'blue.600' }}
            colorScheme={'blue'}
            whiteSpace={'normal'}>
            {bookingEditParams.booking ? 'Buchung aktualisieren' : 'Buchung anlegen'}
          </Button>
          {bookingEditParams.booking && (
            <IconButton
              icon={<AiOutlineClose />}
              aria-label={'stop editing booking'}
              variant="outline"
              colorScheme={'blue'}
              h={'100%'}
              onClick={cancelEditingBooking}
            />
          )}
        </ButtonGroup>
      </Flex>
    </Flex>
  );
};
